/**
 * API Client Module
 * Provides fetch wrappers for all backend API endpoints with:
 * - Error handling for network failures
 * - Request deduplication to prevent duplicate submissions
 * - Loading state management
 */

import authService from './AuthService';
import { getTimezoneHeaders, getAccountTimezone, rememberProtectionContext, notifyStreakProtectionChanged } from './StreakProtection';
import { getLanguage } from '../i18n';

// Base URL for API endpoints
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Backend origin for resolving relative media paths (e.g. /uploads/covers/...)
const BACKEND_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');

function resolveMediaUrl(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_ORIGIN}${url}`;
}

function resolveSceneMedia(scene) {
  if (!scene || !Array.isArray(scene.frames)) return scene;
  return {
    ...scene,
    frames: scene.frames.map((frame) => ({
      ...frame,
      ...(frame?.imageUrl ? { imageUrl: resolveMediaUrl(frame.imageUrl) } : {}),
      ...(frame?.audioUrl ? { audioUrl: resolveMediaUrl(frame.audioUrl) } : {}),
    })),
  };
}

function getUserHeaders(extraHeaders = {}) {
  const token = authService.getAuthToken?.();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'X-App-Language': getLanguage(),
    ...getTimezoneHeaders(),
    ...extraHeaders,
  };
}

const ADMIN_TOKEN_STORAGE_KEY = 'admin_session_token';

function getAdminToken() {
  if (typeof sessionStorage === 'undefined') return '';
  return sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY) || '';
}

function setAdminToken(token) {
  if (typeof sessionStorage === 'undefined') return;
  if (token) {
    sessionStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, token);
  } else {
    sessionStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
  }
}

function getAdminHeaders(extraHeaders = {}) {
  const token = getAdminToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'X-App-Language': getLanguage(),
    ...extraHeaders,
  };
}

// Track pending requests to prevent duplicates
const pendingRequests = new Map();
const TOURNAMENT_SUMMARY_CACHE_MS = 10_000;
let tournamentSummaryCache = null;
const USER_SUMMARY_CACHE_MS = 15_000;
let userSummaryCache = null;

function clearTournamentSummaryCache() {
  tournamentSummaryCache = null;
}

function clearUserSummaryCache() {
  userSummaryCache = null;
}

function marketListingBody({ coverFile, ...listing }) {
  if (!coverFile) return JSON.stringify(listing);
  const data = new FormData();
  Object.entries(listing).forEach(([key, value]) => {
    if (value !== undefined && value !== null) data.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
  });
  data.append('cover', coverFile);
  return data;
}

function noteContentBody({ coverFile, ...payload }) {
  if (!coverFile) return JSON.stringify(payload);
  const data = new FormData();
  data.append('payload', JSON.stringify(payload));
  data.append('cover', coverFile);
  return data;
}

/**
 * Generate a unique key for a request based on method, URL, and body
 * @param {string} method - HTTP method
 * @param {string} url - Request URL
 * @param {Object} body - Request body
 * @returns {string} Unique request key
 */
function getRequestKey(method, url, body, authorization) {
  return `${authorization || ''}:${method}:${url}:${JSON.stringify(body || {})}`;
}

/**
 * Generic fetch wrapper with error handling and request deduplication
 * @param {string} endpoint - API endpoint path
 * @param {Object} options - Fetch options
 * @param {boolean} allowDuplicates - Whether to allow duplicate requests (default: false)
 * @returns {Promise<Object>} Response data
 * @throws {Error} Network or API errors
 */
async function fetchWithErrorHandling(endpoint, options = {}, allowDuplicates = false) {
  const url = `${API_BASE_URL}${endpoint}`;
  const method = options.method || 'GET';
  const body = options.body;
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const requestHeaders = getUserHeaders({
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
  });

  // Generate request key for deduplication
  const requestKey = getRequestKey(method, url, body, requestHeaders.Authorization);

  // Check if this request is already pending (unless duplicates are allowed)
  if (!allowDuplicates && pendingRequests.has(requestKey)) {
    // Return the existing pending request
    return pendingRequests.get(requestKey);
  }

  // Create the fetch promise
  const fetchPromise = (async () => {
    try {
      const response = await fetch(url, {
        ...options,
        headers: requestHeaders,
      });

      const publicAuthRequest = /^\/auth\/(login|signup|forgot-password|reset-password|verify-email)([/?]|$)/.test(endpoint);
      if (response.status === 401 && !publicAuthRequest && !/^\/admin(\/|$)/.test(endpoint)) {
        authService.handleUnauthorized?.(requestHeaders.Authorization?.replace(/^Bearer\s+/i, ''));
      }

      // Parse response body
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      // Handle HTTP errors
      if (!response.ok) {
        const serverMessage = typeof data?.error === 'string' ? data.error : data?.error?.message;
        const error = new Error(
          serverMessage || data?.message || `HTTP ${response.status}: ${response.statusText}`
        );
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return data;
    } catch (error) {
      // Handle network errors (various TypeError messages from fetch failures)
      if (error.name === 'TypeError' && (
        error.message.includes('fetch') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('Network request failed') ||
        error.message.includes('NetworkError')
      )) {
        const networkError = new Error('Network error: Unable to connect to the server. Please check your connection.');
        networkError.isNetworkError = true;
        throw networkError;
      }
      // Re-throw other errors
      throw error;
    } finally {
      // Remove from pending requests when complete
      pendingRequests.delete(requestKey);
    }
  })();

  // Store the pending request
  if (!allowDuplicates) {
    pendingRequests.set(requestKey, fetchPromise);
  }

  return fetchPromise;
}

async function fetchAdminDownload(endpoint) {
  let response;
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, { headers: getAdminHeaders() });
  } catch (_error) {
    throw new Error('Network error: Unable to connect to the server. Please check your connection.');
  }
  if (!response.ok) {
    let data = null;
    try { data = await response.json(); } catch (_error) { /* response may not be JSON */ }
    const error = new Error(data?.error?.message || `Download failed (${response.status})`);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  const disposition = response.headers.get('content-disposition') || '';
  const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || 'download.zip';
  return { blob: await response.blob(), filename };
}

/**
 * API Client object with methods for all endpoints
 */
const apiClient = {
  getDashboardPreviews: () => fetchWithErrorHandling('/flashcards/dashboard-previews', { method: 'GET' }, true),

  /**
   * Get all flash cards
   * @returns {Promise<Array>} Array of flash card objects
   */
  getAllFlashCards: async (options = {}) => {
    const noteId = typeof options?.noteId === 'string' ? options.noteId.trim() : '';
    const status = typeof options?.status === 'string' ? options.status.trim() : '';
    const view = typeof options?.view === 'string' ? options.view.trim() : '';
    const timezone = typeof options?.timezone === 'string' ? options.timezone.trim() : '';
    const params = new URLSearchParams();
    if (noteId) params.set('noteId', noteId);
    if (status) params.set('status', status);
    if (view) params.set('view', view);
    if (timezone) params.set('timezone', timezone);
    if (options.studyKind) params.set('studyKind', options.studyKind);
    const query = params.toString() ? `?${params.toString()}` : '';
    return fetchWithErrorHandling(`/flashcards${query}`, {
      method: 'GET',
    });
  },

  /**
   * Get a specific flash card by ID
   * @param {string} id - Flash card ID
   * @returns {Promise<Object>} Flash card object
   */
  getFlashCardById: async (id) => {
    return fetchWithErrorHandling(`/flashcards/${id}`, {
      method: 'GET',
    }, true); // Allow duplicates for GET requests
  },

  /**
   * Create a new flash card
   * @param {Object} flashCardData - Flash card data
   * @param {string} flashCardData.front - Front content
   * @param {string} flashCardData.back - Back content
   * @returns {Promise<Object>} Created flash card object
   */
  createFlashCard: async (flashCardData) => {
    return fetchWithErrorHandling('/flashcards', {
      method: 'POST',
      body: JSON.stringify(flashCardData),
    });
  },

  /**
   * Update an existing flash card
   * @param {string} id - Flash card ID
   * @param {Object} flashCardData - Updated flash card data
   * @param {string} flashCardData.front - Front content
   * @param {string} flashCardData.back - Back content
   * @returns {Promise<Object>} Updated flash card object
   */
  updateFlashCard: async (id, flashCardData) => {
    const updatedCard = await fetchWithErrorHandling(`/flashcards/${id}`, {
      method: 'PUT',
      body: JSON.stringify(flashCardData),
    });
    return updatedCard;
  },

  getCardTtsStatus: async (id, { source, signal } = {}) => {
    const query = source ? `?source=${encodeURIComponent(source)}` : '';
    return fetchWithErrorHandling(`/tts/cards/${encodeURIComponent(id)}${query}`, {
      method: 'GET',
      signal,
    }, true);
  },

  getReadingPlaylist: (id, { signal } = {}) => fetchWithErrorHandling(`/tts/cards/${encodeURIComponent(id)}/reading-playlist`, { method: 'GET', signal }, true),
  generateReadingUnit: (id, unitId, revision, { signal } = {}) => fetchWithErrorHandling(
    `/tts/cards/${encodeURIComponent(id)}/reading-units/${encodeURIComponent(unitId)}/audio`,
    { method: 'POST', body: JSON.stringify({ revision }), signal }, true),

  generateCardTts: async (id, { source, signal } = {}) => {
    const query = source ? `?source=${encodeURIComponent(source)}` : '';
    return fetchWithErrorHandling(`/tts/cards/${encodeURIComponent(id)}${query}`, {
      method: 'POST',
      signal,
    }, true);
  },

  getVocabularyOverview: () => fetchWithErrorHandling('/vocabulary', { method: 'GET' }, true),
  getVocabularyHomePreview: () => fetchWithErrorHandling('/vocabulary/home-preview', { method: 'GET' }, true),
  getVocabularyStages: () => fetchWithErrorHandling('/vocabulary', { method: 'GET' }, true),
  setVocabularyNoteDirection: (noteId, direction, { operationId } = {}) => fetchWithErrorHandling(`/vocabulary/notes/${encodeURIComponent(noteId)}/direction`, {
    method: 'PATCH', body: JSON.stringify({ direction, operationId }),
  }, true),
  getVocabularyNote: async (noteId) => {
    const data = await fetchWithErrorHandling(`/vocabulary/notes/${encodeURIComponent(noteId)}`, { method: 'GET' }, true);
    const { note, ...metadata } = data;
    return { ...note, ...metadata, noteTitle: note?.title || note?.noteTitle, cards: data.cards || [] };
  },
  startVocabularySession: (body) => fetchWithErrorHandling('/vocabulary/sessions', { method: 'POST', body: JSON.stringify(body) }, true),
  completeVocabularySession: async (sessionId, body) => {
    const result = await fetchWithErrorHandling(`/vocabulary/sessions/${encodeURIComponent(sessionId)}/complete`, {
      method: 'POST', body: JSON.stringify(body),
    }, true);
    clearUserSummaryCache();
    notifyStreakProtectionChanged();
    return result;
  },
  getVocabularySession: (id = 'current') => fetchWithErrorHandling(`/vocabulary/sessions/${encodeURIComponent(id)}`, { method: 'GET' }, true),
  vocabularyAction: async (sessionId, type, body = {}) => {
    const result = await fetchWithErrorHandling(`/vocabulary/sessions/${encodeURIComponent(sessionId)}/actions`, {
      method: 'POST', body: JSON.stringify({ ...body, type }),
    }, true);
    clearUserSummaryCache();
    notifyStreakProtectionChanged();
    return result;
  },
  recordVocabularyTime: (sessionId, body) => fetchWithErrorHandling(`/vocabulary/sessions/${encodeURIComponent(sessionId)}/time`, {
    method: 'POST', body: JSON.stringify(body), keepalive: true,
  }, true),
  finishVocabularySession: async (sessionId, body) => {
    const result = await fetchWithErrorHandling(`/vocabulary/sessions/${encodeURIComponent(sessionId)}/finish`, {
      method: 'POST', body: JSON.stringify(body),
    }, true);
    clearUserSummaryCache();
    notifyStreakProtectionChanged();
    return result;
  },
  getVocabularyAudio: (sessionId, appearanceId) => fetchWithErrorHandling(
    `/vocabulary/sessions/${encodeURIComponent(sessionId)}/audio/${encodeURIComponent(appearanceId)}`, { method: 'GET' }, true),
  fetchVocabularyCover: async (noteId, { signal } = {}) => {
    const response = await fetch(`${API_BASE_URL}/vocabulary/notes/${encodeURIComponent(noteId)}/cover`, {
      method: 'GET', signal, headers: getUserHeaders(),
    });
    if (!response.ok) {
      const error = new Error(`Unable to load vocabulary cover (${response.status})`);
      error.status = response.status;
      throw error;
    }
    return response.blob();
  },

  getWritingToday: () => fetchWithErrorHandling('/writing/today', { method: 'GET' }, true),
  startWriting: () => fetchWithErrorHandling('/writing/start', { method: 'POST' }, true),
  getWritingResult: (examId, batchNumber = 1) => fetchWithErrorHandling(`/writing/exams/${encodeURIComponent(examId)}/result?${new URLSearchParams({ batch: batchNumber })}`, { method: 'GET' }, true),
  getWritingRecords: (params = {}) => fetchWithErrorHandling(`/writing/records?${new URLSearchParams(params)}`, { method: 'GET' }, true),
  getWritingRecord: (id) => fetchWithErrorHandling(`/writing/records/${encodeURIComponent(id)}`, { method: 'GET' }, true),
  getWritingReview: (params = {}) => fetchWithErrorHandling(`/writing/review?${new URLSearchParams(params)}`, { method: 'GET' }, true),
  writingAction: async (exam, item, action, body = {}) => {
    const result = await fetchWithErrorHandling(`/writing/exams/${encodeURIComponent(exam)}/items/${encodeURIComponent(item)}/${action}`, { method: 'POST', body: JSON.stringify(body) }, true);
    clearUserSummaryCache();
    notifyStreakProtectionChanged();
    return result;
  },
  recordWritingTime: async (exam, body) => {
    const result = await fetchWithErrorHandling(`/writing/exams/${encodeURIComponent(exam)}/time`, { method: 'POST', body: JSON.stringify(body), keepalive: true }, true);
    clearUserSummaryCache();
    notifyStreakProtectionChanged();
    return result;
  },
  saveWritingRecord: (id, body) => fetchWithErrorHandling(`/writing/records/${encodeURIComponent(id)}/save`, { method: 'POST', body: JSON.stringify(body) }, true),

  fetchTtsAudioBlob: async (audioUrl, { signal } = {}) => {
    const rawUrl = String(audioUrl || '');
    const url = /^https?:\/\//i.test(rawUrl)
      ? rawUrl
      : rawUrl.startsWith('/api/')
        ? `${BACKEND_ORIGIN}${rawUrl}`
        : `${API_BASE_URL}${rawUrl}`;
    const response = await fetch(url, {
      method: 'GET',
      signal,
      cache: 'default',
      headers: getUserHeaders(),
    });
    if (!response.ok) {
      const error = new Error(`Unable to download listening audio (${response.status})`);
      error.status = response.status;
      throw error;
    }
    return response.blob();
  },

  levelUpFlashCard: async (id, flashCardData) => {
    return fetchWithErrorHandling(`/flashcards/${id}/level-up`, {
      method: 'POST',
      body: JSON.stringify(flashCardData),
    });
  },

  levelUpNote: async (noteId, payload) => fetchWithErrorHandling(`/notes/${encodeURIComponent(noteId)}/level-up`, {
    method: 'POST', body: JSON.stringify(payload),
  }),

  getLevelUpCandidates: async () => (
    fetchWithErrorHandling('/level-up/candidates', { method: 'GET' })
  ),

  getReadingGroupLevelUpCandidates: async (groupId, method) => (
    fetchWithErrorHandling(`/reading-groups/${encodeURIComponent(groupId)}/level-up-candidates${method ? `?method=${encodeURIComponent(method)}` : ''}`, {
      method: 'GET',
    })
  ),

  /**
   * Delete a flash card
   * @param {string} id - Flash card ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  deleteFlashCard: async (id) => {
    return fetchWithErrorHandling(`/flashcards/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Record a practice session (increment practice counter)
   * @param {string} id - Flash card ID
   * @returns {Promise<Object>} Updated flash card object
   */
  recordPractice: async (id) => {
    return fetchWithErrorHandling(`/flashcards/${id}/practice`, {
      method: 'POST',
    });
  },

  /**
   * Record an evaluation for a flash card
   * @param {string} id - Flash card ID
   * @param {number} grade - Evaluation grade (1-5)
   * @returns {Promise<Object>} Updated flash card object
   */
  recordEvaluation: async (id, grade) => {
    return fetchWithErrorHandling(`/flashcards/${id}/evaluate`, {
      method: 'POST',
      body: JSON.stringify({ grade }),
    });
  },

  /**
   * Flush buffered session progress for multiple flash cards in one request.
   * @param {Object} input
   * @param {Array} input.updates - Buffered updates grouped by flash card id
   * @returns {Promise<{success: boolean, updatedCards: Array}>}
   */
  savePracticeProgressBatch: async ({ updates, practiceStartId }) => {
    return fetchWithErrorHandling('/flashcards/session-progress', {
      method: 'POST',
      body: JSON.stringify({ updates, practiceStartId }),
    });
  },

  /**
   * Import flash cards from CSV content
   * @param {string} csvContent - CSV file content
   * @returns {Promise<Object>} Import results with success/failure counts
   */
  importFlashCards: async (csvContent) => {
    return fetchWithErrorHandling('/flashcards/import', {
      method: 'POST',
      body: JSON.stringify({ csvContent }),
    });
  },

  /**
   * Get the server-side drill preset folder tree
   * @returns {Promise<Object>} Preset tree grouped by language and level
   */
  getDrillPresets: async () => {
    return fetchWithErrorHandling('/drill-presets', {
      method: 'GET',
    }, true);
  },

  /**
   * Get parsed content for a selected drill preset
   * @param {string} presetId - Preset ID returned by getDrillPresets
   * @returns {Promise<Object>} Parsed preset content and preview data
   */
  getDrillPresetContent: async (presetId) => {
    return fetchWithErrorHandling(`/drill-presets/content?presetId=${encodeURIComponent(presetId)}`, {
      method: 'GET',
    }, true);
  },

  /**
   * Import a selected drill preset as a fresh drill
   * @param {Object} input - Import request
   * @param {string} input.presetId - Preset ID returned by getDrillPresets
   * @param {string} input.drillName - New drill title
   * @returns {Promise<Object>} Import result
   */
  importDrillPreset: async ({ presetId, drillName }) => {
    const data = await fetchWithErrorHandling('/drill-presets/import', {
      method: 'POST',
      body: JSON.stringify({ presetId, drillName }),
    });
    if (data && data.coverUrl) data.coverUrl = resolveMediaUrl(data.coverUrl);
    return data;
  },

  /**
   * Load the file-based preset library for an audience locale.
   */
  getPresetLibrary: async (locale = 'ko') => {
    const data = await fetchWithErrorHandling(
      `/preset-library?locale=${encodeURIComponent(locale)}`,
      { method: 'GET' },
      true
    );
    return {
      ...data,
      items: (data?.items || []).map((item) => ({
        ...item,
        coverUrl: resolveMediaUrl(item.coverUrl),
      })),
    };
  },

  getLibraryBrowse: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    });
    const data = await fetchWithErrorHandling(`/preset-library/browse?${params}`, { method: 'GET' }, true);
    return {
      ...data,
      ...(Array.isArray(data?.groups) ? {
        groups: data.groups.map((group) => ({
          ...group,
          ...(group?.imageUrl ? { imageUrl: resolveMediaUrl(group.imageUrl) } : {}),
        })),
      } : {}),
    };
  },

  getStudyPath: async (locale = 'ko') => fetchWithErrorHandling(
    `/preset-library/study-path?locale=${encodeURIComponent(locale)}`, { method: 'GET' }, true
  ),

  getPresetLibraryLocales: async () => {
    return fetchWithErrorHandling('/preset-library/locales', {
      method: 'GET',
    }, true);
  },

  /**
   * Load metadata and a small preview for one preset.
   */
  getPresetLibraryItem: async (presetId) => {
    const data = await fetchWithErrorHandling(
      `/preset-library/${encodeURIComponent(presetId)}`,
      { method: 'GET' },
      true
    );
    return {
      ...data,
      coverUrl: resolveMediaUrl(data?.coverUrl),
    };
  },

  setLibraryFavorite: async (sourceType, sourceId, favorite) => {
    const source = sourceType === 'market' ? 'market' : 'preset';
    const path = source === 'market' ? '/library-market' : '/preset-library';
    return fetchWithErrorHandling(
      `${path}/${encodeURIComponent(sourceId)}/favorite`,
      { method: favorite ? 'PUT' : 'DELETE' }
    );
  },

  fetchPresetLibraryCover: async (presetId, { signal } = {}) => {
    const response = await fetch(
      `${API_BASE_URL}/preset-library/${encodeURIComponent(presetId)}/cover`,
      {
        method: 'GET',
        signal,
        headers: getUserHeaders(),
      }
    );
    if (!response.ok) {
      const error = new Error(`Unable to load preset cover (${response.status})`);
      error.status = response.status;
      throw error;
    }
    return response.blob();
  },

  fetchPresetLibraryGroupImage: async (locale, groupId, { signal } = {}) => {
    const response = await fetch(
      `${API_BASE_URL}/preset-library/groups/${encodeURIComponent(locale)}/${encodeURIComponent(groupId)}/image`,
      {
        method: 'GET',
        signal,
        headers: getUserHeaders(),
      }
    );
    if (!response.ok) {
      const error = new Error(`Unable to load preset group image (${response.status})`);
      error.status = response.status;
      throw error;
    }
    return response.blob();
  },

  /**
   * Import a library preset under its catalog title.
   */
  importPresetLibraryItem: async (presetId, { operationId } = {}) => {
    const data = await fetchWithErrorHandling(
      `/preset-library/${encodeURIComponent(presetId)}/import`,
      { method: 'POST', ...(operationId ? { body: JSON.stringify({ operationId }) } : {}) }
    );
    return {
      ...data,
      coverUrl: resolveMediaUrl(data?.coverUrl),
    };
  },

  getPresetLibraryReviews: async (presetId, { page = 1, pageSize = 10 } = {}) => (
    fetchWithErrorHandling(
      `/preset-library/${encodeURIComponent(presetId)}/reviews?page=${page}&pageSize=${pageSize}`,
      { method: 'GET' },
      true
    )
  ),

  savePresetLibraryReview: async (presetId, review) => fetchWithErrorHandling(
    `/preset-library/${encodeURIComponent(presetId)}/review`,
    { method: 'PUT', body: JSON.stringify(review) }
  ),

  getLibraryDashboardPreview: async (locale = 'ko') => fetchWithErrorHandling(
    `/library-market/dashboard-preview?locale=${encodeURIComponent(locale)}`,
    { method: 'GET' },
    true
  ),

  getLibraryMarketOverview: async () => fetchWithErrorHandling('/library-market/overview', {
    method: 'GET',
  }, true),

  getLibraryMarketListings: async ({ query = '', sort = 'latest', page = 1, pageSize = 20, studyKind } = {}) => {
    const params = new URLSearchParams({ sort, page: String(page), pageSize: String(pageSize) });
    if (query) params.set('query', query);
    if (studyKind && studyKind !== 'all') params.set('studyKind', studyKind);
    return fetchWithErrorHandling(`/library-market?${params.toString()}`, { method: 'GET' }, true);
  },

  getLibraryMarketListing: async (listingId) => fetchWithErrorHandling(
    `/library-market/${encodeURIComponent(listingId)}`,
    { method: 'GET' },
    true
  ),

  publishLibraryMarketListing: async (listing) => fetchWithErrorHandling('/library-market', {
    method: 'POST',
    body: marketListingBody(listing),
  }, Boolean(listing.coverFile)),

  updateLibraryMarketListing: async (listingId, updates) => fetchWithErrorHandling(
    `/library-market/${encodeURIComponent(listingId)}`,
    { method: 'PATCH', body: marketListingBody(updates) },
    Boolean(updates.coverFile)
  ),

  withdrawLibraryMarketListing: async (listingId) => fetchWithErrorHandling(
    `/library-market/${encodeURIComponent(listingId)}`,
    { method: 'PATCH', body: JSON.stringify({ status: 'withdrawn' }) }
  ),

  getMarketListingForNote: async (noteId) => fetchWithErrorHandling(
    `/library-market/source-note/${encodeURIComponent(noteId)}`, { method: 'GET' }, true
  ),

  refreshMarketListingFromNote: async (listingId, payload = {}) => fetchWithErrorHandling(
    `/library-market/${encodeURIComponent(listingId)}/refresh-from-note`,
    { method: 'POST', body: marketListingBody(payload) }, Boolean(payload.coverFile)
  ),

  deleteLibraryMarketListing: async (listingId) => fetchWithErrorHandling(
    `/library-market/${encodeURIComponent(listingId)}`,
    { method: 'DELETE' }
  ),

  importLibraryMarketListing: async (listingId, { operationId } = {}) => fetchWithErrorHandling(
    `/library-market/${encodeURIComponent(listingId)}/import`,
    { method: 'POST', ...(operationId ? { body: JSON.stringify({ operationId }) } : {}) }
  ),

  getLibraryMarketReviews: async (listingId, { page = 1, pageSize = 10 } = {}) => (
    fetchWithErrorHandling(
      `/library-market/${encodeURIComponent(listingId)}/reviews?page=${page}&pageSize=${pageSize}`,
      { method: 'GET' },
      true
    )
  ),

  saveLibraryMarketReview: async (listingId, review) => fetchWithErrorHandling(
    `/library-market/${encodeURIComponent(listingId)}/review`,
    { method: 'PUT', body: JSON.stringify(review) }
  ),

  reportLibraryMarketListing: async (listingId, report) => fetchWithErrorHandling(
    `/library-market/${encodeURIComponent(listingId)}/report`,
    { method: 'POST', body: JSON.stringify(report) }
  ),

  fetchLibraryMarketCover: async (listingId, { signal, version } = {}) => {
    const response = await fetch(
      `${API_BASE_URL}/library-market/${encodeURIComponent(listingId)}/cover${version ? `?v=${encodeURIComponent(version)}` : ''}`,
      { method: 'GET', signal, headers: getUserHeaders() }
    );
    if (!response.ok) throw new Error(`Unable to load Market cover (${response.status})`);
    return response.blob();
  },

  /**
   * Save a practice session
   * @param {Object} sessionData - Practice session data
   * @param {string} sessionData.startTime - ISO timestamp of session start
   * @param {string} sessionData.endTime - ISO timestamp of session end
   * @param {number} sessionData.duration - Duration in seconds
   * @param {string} sessionData.timezone - Timezone string
   * @returns {Promise<Object>} Session save result with daily total
   */
  reserveVacation: async (date, operationId) => {
    const result = await fetchWithErrorHandling(`/practice/vacations/${encodeURIComponent(date)}`, { method: 'PUT', body: JSON.stringify({ operationId }) });
    clearUserSummaryCache();
    notifyStreakProtectionChanged();
    return rememberProtectionContext(result);
  },

  cancelVacation: async (date, operationId) => {
    const result = await fetchWithErrorHandling(`/practice/vacations/${encodeURIComponent(date)}`, { method: 'DELETE', body: JSON.stringify({ operationId }) });
    clearUserSummaryCache();
    notifyStreakProtectionChanged();
    return rememberProtectionContext(result);
  },

  savePracticeSession: async (sessionData) => {
    const result = await fetchWithErrorHandling('/practice/session', {
      method: 'POST',
      // Keep the source zone so the server can interpret/rebuild device-date splits in the pinned account zone.
      body: JSON.stringify(sessionData),
    });
    clearUserSummaryCache();
    notifyStreakProtectionChanged();
    return result;
  },

  /**
   * Get daily practice total for a specific date
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {string} timezone - Timezone string (optional)
   * @returns {Promise<Object>} Daily total data
   */
  getDailyPracticeTotal: async (date, timezone) => {
    const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return fetchWithErrorHandling(`/practice/daily?date=${date}&timezone=${encodeURIComponent(tz)}`, {
      method: 'GET',
    }, true);
  },

  startPractice: async ({ timezone, mode = 'normal', source }) => {
    return fetchWithErrorHandling('/practice/start', {
      method: 'POST',
      body: JSON.stringify({ timezone: getAccountTimezone() || timezone, mode, ...(source ? { source } : {}) }),
    });
  },

  importQuickPracticeCard: async (id, payload) => {
    return fetchWithErrorHandling(`/practice/quick/cards/${encodeURIComponent(id)}/import`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Get the next coaching-time message before practice starts
   * @param {Object} input
   * @param {string} input.timezone - IANA timezone string
   * @returns {Promise<{shouldShow: boolean, message: {comment: string, index: number, trainer_Id: number}|null}>}
   */
  getCoachingTime: async ({ timezone, requestId } = {}) => {
    if (requestId) {
      const result = await apiClient.selectScene({ trigger: 'practice.before.coaching', timezone, requestId });
      return { ...result, shouldShow: Boolean(result.scene), message: null };
    }
    const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const result = await fetchWithErrorHandling(`/coaching-time/next?timezone=${encodeURIComponent(tz)}`, {
      method: 'GET',
    }, true);
    if (result?.scene) result.scene = resolveSceneMedia(result.scene);
    return result;
  },

  getTournamentSummary: async () => {
    const userId = authService.getCurrentUserId?.() || '';
    if (
      tournamentSummaryCache &&
      tournamentSummaryCache.userId === userId &&
      tournamentSummaryCache.expiresAt > Date.now()
    ) {
      return tournamentSummaryCache.data;
    }

    const data = await fetchWithErrorHandling('/tournament/summary', {
      method: 'GET',
    });
    tournamentSummaryCache = {
      userId,
      data,
      expiresAt: Date.now() + TOURNAMENT_SUMMARY_CACHE_MS,
    };
    return data;
  },

  getCurrentNotification: async () => {
    return fetchWithErrorHandling('/notifications/current', {
      method: 'GET',
    }, true);
  },

  getTournamentPracticeIntro: async () => {
    const result = await fetchWithErrorHandling('/tournament/practice-intro', {
      method: 'GET',
    }, true);
    if (result?.intro?.scene) result.intro.scene = resolveSceneMedia(result.intro.scene);
    return result;
  },

  getLearningCoaching: async (context) => {
    const result = await fetchWithErrorHandling(`/scenes/coaching?context=${encodeURIComponent(context)}`, { method: 'GET' }, true);
    return { ...result, scenes: (result?.scenes || []).map(resolveSceneMedia) };
  },

  selectScene: async ({ trigger, timezone, requestId }) => {
    const result = await fetchWithErrorHandling('/scenes/select', {
      method: 'POST', body: JSON.stringify({ trigger, timezone, requestId }),
    });
    if (result?.scene) result.scene = resolveSceneMedia(result.scene);
    return result;
  },

  getSceneFrameAudio: async ({ scene, frame, signal }) => {
    const response = await fetch(frame.audioUrl || `${API_BASE_URL}/scenes/playback/audio`, {
      method: frame.audioUrl ? 'GET' : 'POST', signal,
      headers: getUserHeaders(frame.audioUrl ? {} : { 'Content-Type': 'application/json' }),
      ...(frame.audioUrl ? {} : { body: JSON.stringify({ playbackId: scene.playbackId, frameId: frame.id }) }),
    });
    if (!response.ok) throw new Error('Scene audio unavailable');
    return response.blob();
  },

  getRandomScene: async ({ trigger, timezone, opponentImageId, seed, requestId } = {}) => {
    if (requestId) return apiClient.selectScene({ trigger, timezone, requestId });
    const params = new URLSearchParams();
    if (trigger) params.set('trigger', trigger);
    if (timezone) params.set('timezone', timezone);
    if (opponentImageId) params.set('opponentImageId', opponentImageId);
    if (seed) params.set('seed', seed);

    const result = await fetchWithErrorHandling(`/scenes/random?${params.toString()}`, {
      method: 'GET',
    }, true);
    if (result?.scene) result.scene = resolveSceneMedia(result.scene);
    return result;
  },

  getSceneById: async (id, { timezone } = {}) => {
    const params = new URLSearchParams();
    if (timezone) params.set('timezone', timezone);
    const query = params.toString() ? `?${params.toString()}` : '';

    const result = await fetchWithErrorHandling(`/scenes/${encodeURIComponent(id)}${query}`, {
      method: 'GET',
    }, true);
    if (result?.scene) result.scene = resolveSceneMedia(result.scene);
    return result;
  },

  applyToTournament: async ({ characterId }) => {
    const result = await fetchWithErrorHandling('/tournament/apply', {
      method: 'POST',
      body: JSON.stringify({ characterId }),
    });
    clearTournamentSummaryCache();
    return result;
  },

  markTournamentNoticeSeen: async (matchId) => {
    const result = await fetchWithErrorHandling(`/tournament/notices/${encodeURIComponent(matchId)}/seen`, {
      method: 'POST',
    });
    clearTournamentSummaryCache();
    return result;
  },

  /**
   * Get all notes with metadata (including coverUrl as relative path)
   * @returns {Promise<Array>} Array of note objects
   */
  getAllNotes: async ({ studyKind } = {}) => {
    const query = studyKind ? `?studyKind=${encodeURIComponent(studyKind)}` : '';
    return fetchWithErrorHandling(`/notes${query}`, { method: 'GET' }, true);
  },

  /**
   * Create note metadata without requiring a cover image.
   * @param {Object} input
   * @param {string} input.noteId
   * @param {string} input.noteTitle
   * @returns {Promise<Object>} Created note metadata
   */
  createNote: async ({ noteId, noteTitle, questionLocale, learningLocale, isEnabled, description, difficulty, studyKind }) => {
    return fetchWithErrorHandling('/notes', {
      method: 'POST',
      body: JSON.stringify({ noteId, noteTitle, questionLocale, learningLocale, isEnabled, description, difficulty, ...(studyKind ? { studyKind } : {}) }),
    });
  },

  composeNote: async (payload) => fetchWithErrorHandling('/notes/compose', {
    method: 'POST', body: noteContentBody(payload),
  }, Boolean(payload.coverFile)),
  createNoteContent: async (payload) => fetchWithErrorHandling('/notes/compose', {
    method: 'POST', body: noteContentBody(payload),
  }, Boolean(payload.coverFile)),

  updateNoteContent: async (noteId, payload) => fetchWithErrorHandling(`/notes/${encodeURIComponent(noteId)}/content`, {
    method: 'PATCH', body: noteContentBody(payload),
  }, Boolean(payload.coverFile)),

  /**
   * Include or exclude a note from practice pools.
   * @param {string} noteId
   * @param {boolean} isEnabled
   * @returns {Promise<Object>} Updated note state
   */
  setNoteEnabled: async (noteId, isEnabled, options = {}) => {
    return fetchWithErrorHandling(`/notes/${encodeURIComponent(noteId)}/enabled`, {
      method: 'PATCH',
      body: JSON.stringify({ isEnabled, ...options }),
    });
  },

  /**
   * Suggest the question and learning locales for a parsed note draft.
   * @param {Array<Object>} cards
   */
  detectNoteLocales: async (cards) => {
    return fetchWithErrorHandling('/notes/detect-locales', {
      method: 'POST',
      body: JSON.stringify({ cards }),
    });
  },

  /**
   * Persist a new note sort order
   * @param {string[]} noteIds - noteIds in the desired display order
   */
  reorderNotes: async (noteIds, options = {}) => {
    return fetchWithErrorHandling('/notes/reorder', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ noteIds, ...options }),
    });
  },

  deleteNote: async (noteId, options = {}) => fetchWithErrorHandling(`/notes/${encodeURIComponent(noteId)}`, {
    method: 'DELETE', ...(Object.keys(options).length ? { body: JSON.stringify(options) } : {}),
  }),

  /**
   * Get metadata for a specific note (including coverUrl)
   * @param {string} noteId - Note ID
   * @returns {Promise<Object>} Note metadata
   */
  getNoteMetadata: async (noteId) => {
    const data = await fetchWithErrorHandling(`/notes/${noteId}`, { method: 'GET' }, true);
    if (data && data.coverUrl) data.coverUrl = resolveMediaUrl(data.coverUrl);
    return data;
  },

  /**
   * Upload a cover image for a note
   * @param {string} noteId - Note ID
   * @param {File} file - Image file to upload
   * @returns {Promise<Object>} Upload result with coverUrl
   */
  uploadCover: async (noteId, file, options = {}) => {
    const url = `${BACKEND_ORIGIN}/api/notes/${noteId}/cover`;
    const formData = new FormData();
    formData.append('cover', file);
    if (typeof options.onProgress === 'function' && typeof XMLHttpRequest !== 'undefined') {
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);

        Object.entries(getUserHeaders()).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });

        xhr.upload.onprogress = (event) => {
          if (!event.lengthComputable) return;
          options.onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
        };

        xhr.onload = () => {
          let parsed = {};
          try {
            parsed = xhr.responseText ? JSON.parse(xhr.responseText) : {};
          } catch (error) {
            reject(new Error('Invalid upload response'));
            return;
          }

          if (xhr.status < 200 || xhr.status >= 300) {
            reject(new Error(parsed?.error?.message || `HTTP ${xhr.status}`));
            return;
          }

          resolve(parsed);
        };

        xhr.onerror = () => reject(new Error('Network error while uploading cover image'));
        xhr.onabort = () => reject(new Error('Cover image upload was cancelled'));
        xhr.send(formData);
      });
      if (data && data.coverUrl) data.coverUrl = resolveMediaUrl(data.coverUrl);
      return data;
    }

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: getUserHeaders(),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data?.error?.message || `HTTP ${response.status}`);
    if (data && data.coverUrl) data.coverUrl = resolveMediaUrl(data.coverUrl);
    return data;
  },

  /**
   * Remove the cover image for a note
   * @param {string} noteId - Note ID
   * @returns {Promise<Object>} Removal result
   */
  removeCover: async (noteId) => {
    return fetchWithErrorHandling(`/notes/${noteId}/cover`, { method: 'DELETE' });
  },

  /**
   * List app gallery cover images.
   * @returns {Promise<Array>} Gallery images with absolute url and thumbnailUrl
   */
  getCoverGallery: async () => {
    const data = await fetchWithErrorHandling('/cover-gallery', { method: 'GET' }, true);
    return (data?.images || []).map((image) => ({
      ...image,
      url: resolveMediaUrl(image.url),
      thumbnailUrl: resolveMediaUrl(image.thumbnailUrl || image.url),
    }));
  },

  /**
   * Select an app gallery cover image for a note.
   * @param {string} noteId - Note ID
   * @param {string} filename - Gallery image filename
   * @returns {Promise<Object>} Update result with absolute coverUrl
   */
  applyGalleryCover: async (noteId, filename) => {
    const data = await fetchWithErrorHandling(`/notes/${noteId}/cover/gallery`, {
      method: 'PUT',
      body: JSON.stringify({ filename }),
    });
    if (data && data.coverUrl) data.coverUrl = resolveMediaUrl(data.coverUrl);
    return data;
  },

  signup: async ({ email, nickname, password, englishLevel, legacyUserId, language }) => {
    return fetchWithErrorHandling('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, nickname, password, englishLevel, legacyUserId, language }),
    });
  },

  login: async ({ email, password }) => {
    return fetchWithErrorHandling('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  logout: async () => {
    return fetchWithErrorHandling('/auth/logout', { method: 'POST' }, true);
  },

  verifyEmail: async (token) => {
    return fetchWithErrorHandling(`/auth/verify-email?token=${encodeURIComponent(token)}`, { method: 'GET' }, true);
  },

  forgotPassword: async ({ email }) => {
    return fetchWithErrorHandling('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  resetPassword: async ({ token, password }) => {
    return fetchWithErrorHandling('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  },

  changePassword: async ({ currentPassword, newPassword }) => {
    return fetchWithErrorHandling('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  deleteAccount: async ({ password }) => {
    return fetchWithErrorHandling('/auth/account', {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    });
  },

  getCurrentUser: async () => {
    return fetchWithErrorHandling('/users/me', { method: 'GET' }, true);
  },

  getCurrentUserSummary: async () => {
    const userId = authService.getCurrentUserId?.() || '';
    if (
      userSummaryCache &&
      userSummaryCache.userId === userId &&
      userSummaryCache.expiresAt > Date.now()
    ) {
      return userSummaryCache.data;
    }

    const data = await fetchWithErrorHandling('/users/me/summary', { method: 'GET' });
    rememberProtectionContext(data?.streakProtection);
    userSummaryCache = {
      userId,
      data,
      expiresAt: Date.now() + USER_SUMMARY_CACHE_MS,
    };
    return data;
  },

  updateCurrentUser: async ({ nickname }) => {
    const result = await fetchWithErrorHandling('/users/me', {
      method: 'PATCH',
      body: JSON.stringify({ nickname }),
    });
    clearUserSummaryCache();
    return result;
  },

  getAdminToken,

  clearAdminToken: () => {
    setAdminToken('');
  },

  adminLogin: async ({ id, password }) => {
    const result = await fetchWithErrorHandling('/admin/login', {
      method: 'POST',
      body: JSON.stringify({ id, password }),
    });
    setAdminToken(result?.token || '');
    return result;
  },

  getAdminUsers: async ({ query = '', status = 'all' } = {}) => {
    const params = new URLSearchParams();
    if (query) params.set('query', query);
    if (status) params.set('status', status);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return fetchWithErrorHandling(`/admin/users${queryString}`, {
      method: 'GET',
      headers: getAdminHeaders(),
    }, true);
  },

  createAdminUser: async ({ userId, password, nickname, language, membershipPlan = 'user' }) => {
    return fetchWithErrorHandling('/admin/users', {
      method: 'POST',
      headers: getAdminHeaders(),
      body: JSON.stringify({ userId, password, nickname, language, membershipPlan }),
    });
  },

  updateAdminUserMembership: async (userId, membershipPlan) => {
    return fetchWithErrorHandling(`/admin/users/${encodeURIComponent(userId)}/membership`, {
      method: 'PATCH',
      headers: getAdminHeaders(),
      body: JSON.stringify({ membershipPlan }),
    });
  },

  deleteAdminUser: async (userId) => {
    return fetchWithErrorHandling(`/admin/users/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
      headers: getAdminHeaders(),
    });
  },

  getAdminMarketListings: async ({ query = '', status = 'all', reportedOnly = false, page = 1, pageSize = 20 } = {}) => {
    const params = new URLSearchParams({ status, page: String(page), pageSize: String(pageSize) });
    if (query) params.set('query', query);
    if (reportedOnly) params.set('reportedOnly', 'true');
    return fetchWithErrorHandling(`/admin/market/listings?${params.toString()}`, {
      method: 'GET', headers: getAdminHeaders(),
    }, true);
  },

  getAdminMarketListing: async (listingId) => fetchWithErrorHandling(
    `/admin/market/listings/${encodeURIComponent(listingId)}`,
    { method: 'GET', headers: getAdminHeaders() },
    true
  ),

  fetchAdminMarketCover: async (listingId, { signal } = {}) => {
    const response = await fetch(
      `${API_BASE_URL}/admin/market/listings/${encodeURIComponent(listingId)}/cover`,
      { method: 'GET', signal, headers: getAdminHeaders() }
    );
    if (!response.ok) throw new Error(`Unable to load Market cover (${response.status})`);
    return response.blob();
  },

  removeAdminMarketListing: async (listingId) => fetchWithErrorHandling(
    `/admin/market/listings/${encodeURIComponent(listingId)}`,
    { method: 'DELETE', headers: getAdminHeaders() }
  ),

  getAdminMarketReviews: async ({ targetType = '', query = '', status = 'all', page = 1, pageSize = 20 } = {}) => {
    const params = new URLSearchParams({ status, page: String(page), pageSize: String(pageSize) });
    if (targetType) params.set('targetType', targetType);
    if (query) params.set('query', query);
    return fetchWithErrorHandling(`/admin/market/reviews?${params.toString()}`, {
      method: 'GET', headers: getAdminHeaders(),
    }, true);
  },

  removeAdminMarketReview: async (reviewId) => fetchWithErrorHandling(
    `/admin/market/reviews/${encodeURIComponent(reviewId)}`,
    { method: 'DELETE', headers: getAdminHeaders() }
  ),

  updateAdminMarketReport: async (reportId, status) => fetchWithErrorHandling(
    `/admin/market/reports/${encodeURIComponent(reportId)}`,
    { method: 'PATCH', headers: getAdminHeaders(), body: JSON.stringify({ status }) }
  ),

  getAdminPresetLibraryStatus: async () => {
    return fetchWithErrorHandling('/admin/preset-library/status', {
      method: 'GET',
      headers: getAdminHeaders(),
    }, true);
  },

  exportAdminPresetLibrary: async () => fetchAdminDownload('/admin/preset-library/export'),

  validateAdminPresetLibrary: async (bundle) => {
    const formData = new FormData();
    formData.append('bundle', bundle);
    return fetchWithErrorHandling('/admin/preset-library/validate', {
      method: 'POST',
      headers: getAdminHeaders(),
      body: formData,
    });
  },

  publishAdminPresetLibrary: async ({ releaseId, expectedActiveReleaseId, confirmation }) => {
    return fetchWithErrorHandling('/admin/preset-library/publish', {
      method: 'POST',
      headers: getAdminHeaders(),
      body: JSON.stringify({ releaseId, expectedActiveReleaseId, confirmation }),
    });
  },

  rollbackAdminPresetLibrary: async ({ confirmation }) => {
    return fetchWithErrorHandling('/admin/preset-library/rollback', {
      method: 'POST',
      headers: getAdminHeaders(),
      body: JSON.stringify({ confirmation }),
    });
  },

  getAdminNotifications: async () => fetchWithErrorHandling('/admin/notifications', {
    method: 'GET',
    headers: getAdminHeaders(),
  }, true),

  updateAdminNotification: async (language, notification) => fetchWithErrorHandling(`/admin/notifications/${encodeURIComponent(language)}`, {
    method: 'PUT',
    headers: getAdminHeaders(),
    body: JSON.stringify(notification),
  }),

  getAdminScenes: async ({ query = '', locale = '', trigger = '', page = 1, pageSize = 25 } = {}) => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (query) params.set('query', query);
    if (locale) params.set('locale', locale);
    if (trigger) params.set('trigger', trigger);
    return fetchWithErrorHandling(`/admin/scenes?${params.toString()}`, {
      method: 'GET',
      headers: getAdminHeaders(),
    }, true);
  },

  getAdminPresetBrowser: async () => fetchWithErrorHandling('/admin/preset-browser', {
    method: 'GET', headers: getAdminHeaders(),
  }, true),
  getAdminSceneBrowser: async () => fetchWithErrorHandling('/admin/scene-browser', {
    method: 'GET', headers: getAdminHeaders(),
  }, true),
  getAdminScenePreview: async id => {
    const result = await fetchWithErrorHandling(`/admin/scene-browser/${encodeURIComponent(id)}`, {
      method: 'GET', headers: getAdminHeaders(),
    }, true);
    return { ...result, scene: resolveSceneMedia(result.scene) };
  },
  getAdminSceneFrameAudio: async ({ scene, frame, signal }) => {
    const response = await fetch(frame.audioUrl || `${API_BASE_URL}/admin/scene-browser/audio`, {
      method: frame.audioUrl ? 'GET' : 'POST', signal,
      headers: getAdminHeaders(frame.audioUrl ? {} : { 'Content-Type': 'application/json' }),
      ...(frame.audioUrl ? {} : { body: JSON.stringify({ playbackId: scene.playbackId, frameId: frame.id }) }),
    });
    if (!response.ok) throw new Error('Scene audio unavailable');
    return response.blob();
  },

  getAdminSceneEvents: async () => fetchWithErrorHandling('/admin/scene-selection/events', {
    method: 'GET', headers: getAdminHeaders(),
  }, true),
  saveAdminSceneEvent: async (id, event) => fetchWithErrorHandling(`/admin/scene-selection/events${id ? `/${encodeURIComponent(id)}` : ''}`, {
    method: id ? 'PUT' : 'POST', headers: getAdminHeaders(), body: JSON.stringify(event),
  }),
  previewAdminSceneSelection: async input => fetchWithErrorHandling('/admin/scene-selection/preview', {
    method: 'POST', headers: getAdminHeaders(), body: JSON.stringify(input),
  }),

  getAdminSceneLibraryStatus: async () => fetchWithErrorHandling('/admin/scenes/status', {
    method: 'GET',
    headers: getAdminHeaders(),
  }, true),

  exportAdminSceneLibrary: async () => fetchAdminDownload('/admin/scenes/export'),

  validateAdminSceneLibrary: async (bundle) => {
    const formData = new FormData();
    formData.append('bundle', bundle);
    return fetchWithErrorHandling('/admin/scenes/validate', {
      method: 'POST',
      headers: getAdminHeaders(),
      body: formData,
    });
  },

  publishAdminSceneLibrary: async ({ releaseId, expectedActiveReleaseId, confirmation }) => fetchWithErrorHandling('/admin/scenes/publish', {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify({ releaseId, expectedActiveReleaseId, confirmation }),
  }),

  rollbackAdminSceneLibrary: async ({ confirmation }) => fetchWithErrorHandling('/admin/scenes/rollback', {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify({ confirmation }),
  }),

  /**
   * Get the number of pending requests
   * Useful for loading state management
   * @returns {number} Number of pending requests
   */
  getPendingRequestCount: () => {
    return pendingRequests.size;
  },

  /**
   * Check if a specific request is pending
   * @param {string} method - HTTP method
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body (will be stringified)
   * @returns {boolean} True if request is pending
   */
  isRequestPending: (method, endpoint, body) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const bodyString = body ? JSON.stringify(body) : undefined;
    const requestKey = getRequestKey(method, url, bodyString);
    return pendingRequests.has(requestKey);
  },
};

export default apiClient;
